<script lang="ts" setup>import { ref as _ref } from 'vue';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faXmark } from '@fortawesome/pro-solid-svg-icons'

withDefaults(defineProps<{
  image?: string
  icon?: IconDefinition
  emoji?: string
  label: string
  subtext?: string
  rightIcon?: IconDefinition
  removable?: boolean
  size?: 'xs' | 'sm' | 'md'
  variant?: 'default' | 'selected' | 'grey' | 'unselected'
}>(), { size: 'md',variant: 'default',removable: false, })

const emit = defineEmits<{
  remove: []
}>()

// Signed icon URLs expire, so fall back to the emoji rather than a broken image.
let imageFailed = _ref(false)

watch(() => __props.image, () => { imageFailed.value = false })
</script>

<template>
  <div
    class="g-tag"
    :class="[
      `g-tag--${size}`,
      `g-tag--${variant}`,
    ]"
  >
    <slot name="left">
      <div v-if="image || icon || emoji" class="g-tag__left">
        <img
          v-if="image && !imageFailed"
          :src="image"
          class="g-tag__image"
          alt=""
          @error="imageFailed = true"
        >
        <FontAwesomeIcon
          v-else-if="icon"
          :icon="icon"
          class="g-tag__icon"
        />
        <span
          v-else-if="emoji"
          class="g-tag__emoji"
        >{{ emoji }}</span>
      </div>
    </slot>
    <div class="g-tag__content">
      <span class="g-tag__label">{{ label }}</span>
      <template v-if="subtext">
        <span class="g-tag__dot">•</span>
        <span class="g-tag__subtext">{{ subtext }}</span>
      </template>
      <slot name="right">
        <FontAwesomeIcon
          v-if="rightIcon"
          :icon="rightIcon"
          class="g-tag__right-icon"
        />
      </slot>
      <slot name="remove">
        <button
          v-if="removable"
          type="button"
          class="g-tag__remove"
          :aria-label="`Remove ${label}`"
          @click.stop="emit('remove')"
        >
          <FontAwesomeIcon :icon="faXmark"/>
        </button>
      </slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
.g-tag {
  align-items: center;
  border-radius: 2rem;
  display: inline-flex;
  gap: 0.5rem;
  padding: 0.625rem 1rem;

  &--default {
    background-color: var(--color-bg-surface-blue);
    border: 1px solid var(--color-bg-surface-blue);
  }

  &--selected {
    background-color: var(--color-bg-surface-blue);
    border: 1px solid var(--color-bg-surface-blue);
  }

  &--grey {
    background-color: var(--grey-200);
    border: 1px solid var(--grey-200);
  }

  &--unselected {
    background-color: white;
    border: 1px solid var(--grey-300);
  }

  // Extra-compact: used on recommendation cards so two badges fit per row in
  // narrow 2-up mobile grids instead of stacking one-per-line.
  &--xs {
    gap: 0.25rem;
    padding: 0.1875rem 0.4375rem;

    .g-tag__image,
    .g-tag__placeholder {
      height: 1rem;
      width: 1rem;
    }

    .g-tag__icon,
    .g-tag__emoji {
      font-size: 1rem;
      height: 1rem;
      width: 1rem;
    }

    .g-tag__content {
      gap: 0.25rem;
    }

    .g-tag__label {
      font-size: 0.75rem;
    }

    .g-tag__subtext {
      font-size: 0.6875rem;
    }
  }

  &--sm {
    padding: 0.5rem 0.75rem;

    .g-tag__image {
      height: 1.25rem;
      width: 1.25rem;
    }

    .g-tag__icon {
      font-size: 1.25rem;
      height: 1.25rem;
      width: 1.25rem;
    }

    .g-tag__placeholder {
      height: 1.25rem;
      width: 1.25rem;
    }

    .g-tag__emoji {
      font-size: 1.25rem;
      height: 1.25rem;
      width: 1.25rem;
    }

    .g-tag__label {
      font-size: 0.875rem;
    }

    .g-tag__subtext {
      font-size: 0.75rem;
    }
  }

  &--md {
    .g-tag__image {
      height: 1.5rem;
      width: 1.5rem;
    }

    .g-tag__icon {
      font-size: 1.5rem;
      height: 1.5rem;
      width: 1.5rem;
    }
  }
}

.g-tag__left {
  align-items: center;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
}

.g-tag__image {
  border-radius: 0.25rem;
  height: 1.5rem;
  object-fit: cover;
  width: 1.5rem;
}

.g-tag__icon {
  font-size: 1.5rem;
  height: 1.5rem;
  width: 1.5rem;
}

.g-tag__placeholder {
  background-color: var(--grey-400);
  border-radius: 0.25rem;
  height: 1.5rem;
  width: 1.5rem;
}

.g-tag__emoji {
  align-items: center;
  display: flex;
  font-size: 1.5rem;
  height: 1.5rem;
  justify-content: center;
  width: 1.5rem;
}

.g-tag__content {
  align-items: center;
  display: flex;
  flex: 1;
  gap: 0.5rem;
}

.g-tag__label {
  font-size: 0.9375rem;
  font-weight: 600;
}

.g-tag--default .g-tag__label {
  color: var(--color-text-primary-dark);
}

.g-tag--selected .g-tag__label {
  color: var(--color-text-active);
}

.g-tag--grey .g-tag__label {
  color: var(--color-text-primary-dark);
}

.g-tag--unselected .g-tag__label {
  color: var(--color-text-primary-dark);
}

.g-tag__dot {
  color: var(--grey-500);
  font-size: 0.875rem;
}

.g-tag__subtext {
  color: var(--grey-500);
  font-size: 0.875rem;
  margin-left: auto;
}

.g-tag__right-icon {
  color: var(--green-500);
  flex-shrink: 0;
  font-size: 1.125rem;
  margin-left: auto;
}

.g-tag__remove {
  align-items: center;
  color: var(--grey-500);
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  font-size: 0.875rem;
  margin-left: 0.25rem;

  &:hover {
    color: var(--color-text-primary-dark);
  }
}
</style>
